/**
 * Üst menü çubuğu: kök modüller başlığın ortasında yatay, klasörler açılır menü.
 *
 * Başlıkta logo, firma adı, kur ve araç düğmeleriyle aynı satırı paylaşıyor; ayrı bir
 * satır açmak dikey yer harcardı (eski 1024×768 monitörler). Sığmazsa kademeli daralır
 * (bkz. useTopNavFit): önce bütün modüller yalnız simge (ad ipucunda), o da sığmazsa
 * sığanlar simge, kalanı "…" menüsünde. Kullanıcı "yalnız simge"yi başlıktaki düğmeyle
 * kalıcı da seçebilir (sol menüdeki daraltma gibi).
 *
 * Eskiden sığmayanlar yatay kaydırmaya düşüyordu — sağda kalan modüller görünmüyor,
 * kaydırma çubuğu da olmadığı için varlıkları fark edilmiyordu.
 *
 * Klasörlerin içi dar şeritteki gibi YANA açılır (#480): aşağı açılan ağaç, panelin
 * yüksekliğini aşıp kaydırma istiyordu ve alt klasörü açmak için önce tıklamak gerekiyordu.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Tooltip } from '@/shared/ui/Tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import type { UserScreenNode } from '@/shared/types/system';
import { useMenu, isLeaf } from './useMenu';
import { NavIcon } from './NavIcon';
import { FlyoutItems, holds } from './FlyoutItems';
import { useTopNavFit } from './useTopNavFit';
import { useOpenScreen } from '../tabs/useOpenScreen';
import { useTabsStore } from '../tabs/tabsStore';

const ITEM =
  'hover:bg-bg-hover focus-visible:ring-ring/40 flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none';
/** Simge düğmesi: genişliği sabit (ölçülmüyor) — useTopNavFit ICON_W ile aynı olmalı. */
const ICON_ITEM =
  'hover:bg-bg-hover focus-visible:ring-ring/40 flex h-8 w-8 shrink-0 items-center justify-center rounded-md focus-visible:ring-2 focus-visible:outline-none';
const ICON_W = 32;
const OVERFLOW_W = 56;
const GAP = 4;
const ACTIVE = 'bg-brand-500/10 text-brand-700 dark:text-brand-400 font-medium';
const OPEN = 'data-[state=open]:bg-bg-hover';

export function TopNavBar() {
  const { t } = useTranslation();
  const { tree } = useMenu();
  const openScreen = useOpenScreen();
  const compact = usePrefsStore((s) => s.prefs.topNavCompact);
  const activeTab = useTabsStore((s) => s.tabs.find((x) => x.tabId === s.activeTabId));
  const activeScreenId = activeTab?.screenId ?? null;

  const barRef = useRef<HTMLElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const fit = useTopNavFit(barRef, rulerRef, tree.length, {
    iconWidth: ICON_W,
    overflowWidth: OVERFLOW_W,
    gap: GAP,
    compact,
  });
  const icons = fit.mode === 'icons';
  const visible = tree.slice(0, fit.visible);
  const overflow = tree.slice(fit.visible);

  const isActive = (n: UserScreenNode) => n.screenId === activeScreenId || holds(n, activeScreenId);

  const renderCaptioned = (n: UserScreenNode) =>
    isLeaf(n) ? (
      <button
        key={n.screenId}
        type="button"
        onClick={() => openScreen(n)}
        aria-current={n.screenId === activeScreenId ? 'page' : undefined}
        className={cn(ITEM, isActive(n) && ACTIVE)}
      >
        <NavIcon node={n} className="h-4 w-4 shrink-0" />
        {n.caption}
      </button>
    ) : (
      <DropdownMenu key={n.screenId}>
        <DropdownMenuTrigger asChild>
          {/* Aktif ekranın modülü işaretli kalır: hangi modülde olduğunu bir bakışta göster. */}
          <button type="button" className={cn(ITEM, OPEN, isActive(n) && ACTIVE)}>
            <NavIcon node={n} className="h-4 w-4 shrink-0" />
            {n.caption}
            <ChevronDown className="text-fg-subtle h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[80vh] w-64 overflow-y-auto">
          <FlyoutItems nodes={n.children} activeScreenId={activeScreenId} onOpen={openScreen} />
        </DropdownMenuContent>
      </DropdownMenu>
    );

  const renderIcon = (n: UserScreenNode) =>
    isLeaf(n) ? (
      <Tooltip key={n.screenId} content={n.caption}>
        <button
          type="button"
          onClick={() => openScreen(n)}
          aria-label={n.caption}
          aria-current={n.screenId === activeScreenId ? 'page' : undefined}
          className={cn(ICON_ITEM, isActive(n) && ACTIVE)}
        >
          <NavIcon node={n} className="h-5 w-5" />
        </button>
      </Tooltip>
    ) : (
      <DropdownMenu key={n.screenId}>
        <Tooltip content={n.caption}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={n.caption}
              className={cn(ICON_ITEM, OPEN, isActive(n) && ACTIVE)}
            >
              <NavIcon node={n} className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent align="start" className="max-h-[80vh] w-64 overflow-y-auto">
          <DropdownMenuLabel>{n.caption}</DropdownMenuLabel>
          <FlyoutItems nodes={n.children} activeScreenId={activeScreenId} onOpen={openScreen} />
        </DropdownMenuContent>
      </DropdownMenu>
    );

  return (
    <nav
      ref={barRef}
      aria-label={t('shell.menu')}
      className="relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
    >
      {/* Cetvel: bütün modüller adıyla, görünmez; sığdırma hesabı buradan ölçülür. */}
      <div
        ref={rulerRef}
        aria-hidden="true"
        data-testid="topnav-ruler"
        className="pointer-events-none invisible absolute top-0 left-0 flex w-max items-center gap-1"
      >
        {tree.map((n) => (
          <span key={n.screenId} className={ITEM}>
            <NavIcon node={n} className="h-4 w-4 shrink-0" />
            {n.caption}
            {!isLeaf(n) ? <ChevronDown className="h-3.5 w-3.5" /> : null}
          </span>
        ))}
      </div>
      {visible.map(icons ? renderIcon : renderCaptioned)}
      {overflow.length ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('shell.moreModules')}
              title={t('shell.moreModules')}
              className={cn(ITEM, OPEN, overflow.some(isActive) && ACTIVE)}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="text-fg-subtle tabular-nums">{overflow.length}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[80vh] w-64 overflow-y-auto">
            <DropdownMenuLabel>{t('shell.moreModules')}</DropdownMenuLabel>
            {/* Taşan kökler kendi alt ağaçlarıyla birlikte iner: menüde kaybolmasınlar. */}
            <FlyoutItems nodes={overflow} activeScreenId={activeScreenId} onOpen={openScreen} />
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </nav>
  );
}
